import React from 'react';
import {
    Document,
    Page,
    View,
    Text,
    Image,
    StyleSheet,
    Font,
} from '@react-pdf/renderer';
import { formatDateddMMMyyyy } from '../../widgets/DateTime';

const C = {
    darkBlue: '#1a3a5c',
    lightBlue: '#7ec8e3',
    accentBlue: '#2980b9',
    altRow: '#e8f4fb',
    lightGray: '#f5f5f5',
    borderGray: '#cccccc',
    text: '#333333',
    muted: '#555555',
    white: '#ffffff',
};

// ── Planes (estáticos — ajusta si vienen del backend) ─────────────────────────
const PLANS = [
    { plan: 'Plan 0', folios: '500', price: '$158,000' },
    { plan: 'Plan 1', folios: '8,000', price: '$642,000' },
    { plan: 'Plan 2', folios: '20,000', price: '$1,122,000' },
    { plan: 'Plan 3', folios: '50,000', price: '$2,084,000' },
    { plan: 'Plan 4', folios: '120,000', price: '$3,526,000' },
    { plan: 'Plan 5', folios: '250,000', price: '$6,412,000' },
    { plan: 'Plan 6', folios: '500,000', price: '$10,419,000' },
    { plan: 'Plan 7', folios: '1,000,000', price: '$17,630,000' },
    { plan: 'Plan 8', folios: '2,000,000', price: '$24,041,000' },
    { plan: 'Plan 9', folios: '4,000,000', price: '$44,876,000' },
    { plan: 'Plan 10', folios: '8,000,000', price: '$72,123,000' },
    { plan: 'Plan 11', folios: '12,000,000', price: '$96,164,000' },
];

const TERMS = [
    'Los precios indicados en la tabla corresponden a los Planes TAE-COL SAS vigentes para el año 2026 y están expresados en pesos colombianos (COP).',
    'El plan sugerido se basa en el análisis estadístico del consumo del cliente durante el período indicado. TAE-COL SAS no garantiza que el consumo futuro sea igual al registrado.',
    'La vigencia de la propuesta comercial es de 30 días calendario a partir de la fecha de generación.',
    'La autorización de esta propuesta implica la aceptación de los términos y condiciones del contrato de servicios TAE-COL SAS.',
];

// ── Estilos ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
    // Página
    page: {
        fontFamily: 'Helvetica',
        fontSize: 8.5,
        color: C.text,
        paddingTop: 120,   // altura header (108) + 1 de respiro
        paddingBottom: 55,   // altura footer (38) + margen
        paddingLeft: 47,   // 0.65 inch ≈ 46.8pt
        paddingRight: 47,
    },

    // ── Header (posición absoluta, se repite fixed) ───────────────────────────
    headerBar: {
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 108,
        backgroundColor: C.darkBlue,
    },
    headerStripe: {
        position: 'absolute', top: 108, left: 0, right: 0,
        height: 4,
        backgroundColor: C.lightBlue,
    },
    headerLogo: {
        position: 'absolute',
        top: 8, left: 14,
        width: 92, height: 92,
    },
    headerCompany: {
        position: 'absolute',
        top: 46, left: 114,       // H - 46 en ReportLab ≡ top: 46 desde arriba
        color: C.white,
        fontFamily: 'Helvetica-Bold',
        fontSize: 16,
    },
    headerSlogan: {
        position: 'absolute',
        top: 64, left: 114,
        color: C.white,
        fontSize: 9.5,
    },
    headerDocTitle: {
        position: 'absolute',
        top: 44, right: 25,
        color: C.white,
        fontFamily: 'Helvetica-Bold',
        fontSize: 11,
    },
    headerDate: {
        position: 'absolute',
        top: 60, right: 25,
        color: C.white,
        fontSize: 8,
    },

    // ── Footer (posición absoluta, se repite fixed) ───────────────────────────
    footerBar: {
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: 38,
        backgroundColor: C.darkBlue,
    },
    footerStripe: {
        position: 'absolute', bottom: 38, left: 0, right: 0,
        height: 2,
        backgroundColor: C.lightBlue,
    },
    footerLeft1: {
        position: 'absolute', bottom: 22, left: 25,
        color: C.white, fontSize: 7.5,
    },
    footerLeft2: {
        position: 'absolute', bottom: 11, left: 25,
        color: C.white, fontSize: 7.5,
    },
    footerPage: {
        position: 'absolute', bottom: 16, right: 25,
        color: C.white,
        fontFamily: 'Helvetica-Bold',
        fontSize: 8,
    },

    // ── Secciones ─────────────────────────────────────────────────────────────
    sectionBar: {
        backgroundColor: C.darkBlue,
        paddingTop: 5, paddingBottom: 5,
        paddingLeft: 8, paddingRight: 8,
        marginBottom: 4,
    },
    sectionTitle: {
        color: C.white,
        fontFamily: 'Helvetica-Bold',
        fontSize: 9,
    },

    // ── Campos ────────────────────────────────────────────────────────────────
    fieldRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        marginBottom: 8,
        flexWrap: 'nowrap',
    },
    fieldLabel: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 8,
        color: C.darkBlue,
        paddingTop: 6,
        flexShrink: 0,
    },
    fieldValue: {
        fontSize: 8.5,
        borderBottom: `0.8px solid ${C.borderGray}`,
        paddingBottom: 2,
        paddingTop: 6,
        flexShrink: 1,
    },

    // ── Tabla de planes ───────────────────────────────────────────────────────
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: C.darkBlue,
        paddingTop: 4, paddingBottom: 4,
        paddingLeft: 6, paddingRight: 6,
    },
    thCell: {
        color: C.white,
        fontFamily: 'Helvetica-Bold',
        fontSize: 8.5,
        textAlign: 'center',
    },
    tableRow: {
        flexDirection: 'row',
        paddingTop: 4, paddingBottom: 4,
        paddingLeft: 6, paddingRight: 6,
        borderBottom: `0.5px solid #aaaaaa`,
    },
    tdCell: {
        fontSize: 8.5,
        textAlign: 'center',
    },
    tdRight: {
        fontSize: 8.5,
        textAlign: 'right',
    },

    // Anchos de columnas de la tabla
    col25: { width: '25%' },
    col40: { width: '40%' },
    col35: { width: '35%' },

    // ── Caja de pronóstico ────────────────────────────────────────────────────
    forecastBox: {
        backgroundColor: '#eaf4fb',
        border: `1px solid ${C.accentBlue}`,
        padding: 8,
        marginBottom: 6,
    },
    forecastText: {
        fontSize: 8.5,
        lineHeight: 1.6,
        color: C.text,
    },
    forecastBold: {
        fontFamily: 'Helvetica-Bold',
    },

    // ── Términos ──────────────────────────────────────────────────────────────
    termRow: {
        flexDirection: 'row',
        marginBottom: 4,
    },
    termNum: {
        color: C.accentBlue,
        fontFamily: 'Helvetica-Bold',
        fontSize: 8.5,
        width: 14,
    },
    termText: {
        flex: 1,
        fontSize: 8.5,
        lineHeight: 1.5,
        textAlign: 'justify',
        color: C.text,
    },

    // ── Firma ─────────────────────────────────────────────────────────────────
    sigBlock: {
        flexDirection: 'row',
        gap: 20,
        marginTop: 4,
    },
    sigCol: {
        flex: 1,
    },
    sigLine: {
        borderBottom: `0.8px solid ${C.darkBlue}`,
        height: 30,
        marginBottom: 4,
    },
    sigSubLabel: {
        fontSize: 7.5,
        color: C.muted,
        marginBottom: 8,
    },

    // ── Nota de sugerencia de plan ────────────────────────────────────────────
    suggestedPlanNote: {
        backgroundColor: '#d4e9f7',
        border: `1px solid ${C.lightBlue}`,
        padding: 6,
        marginTop: 6,
        marginBottom: 6,
    },
    suggestedPlanNoteText: {
        fontSize: 7.5,
        color: C.darkBlue,
        lineHeight: 1.4,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'left',
    },

    // ── Aviso confidencialidad ────────────────────────────────────────────────
    noticeBox: {
        backgroundColor: C.lightGray,
        border: `0.5px solid ${C.borderGray}`,
        padding: 5,
        marginTop: 12,
    },
    noticeText: {
        fontSize: 7.5,
        color: '#666666',
        textAlign: 'center',
        fontFamily: 'Helvetica-Oblique',
    },

    spacer6: { height: 6 },
    spacer10: { height: 10 },
    spacer18: { height: 18 },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Barra azul de título de sección */
const SectionHeader = ({ title }) => (
    <View style={s.sectionBar}>
        <Text style={s.sectionTitle}>{title}</Text>
    </View>
);

/** Fila etiqueta + línea de valor rellenable */
const FieldRow = ({ label, value = '', labelWidth = '30%', valueWidth = null }) => (
    <View style={s.fieldRow}>
        <Text style={[s.fieldLabel, { width: labelWidth }]}>{label}</Text>
        <Text style={[s.fieldValue, valueWidth ? { flex: 0, width: valueWidth } : {}]}>
            {value}
        </Text>
    </View>
);

/** Header fijo — se repite en cada página */
const PageHeader = ({ logoBase64, date }) => (
    <>
        <View style={s.headerBar} fixed />
        <View style={s.headerStripe} fixed />
        {logoBase64 && (
            <Image src={logoBase64} style={s.headerLogo} fixed />
        )}
        <Text style={s.headerCompany} fixed>TAE-COL SAS</Text>
        <Text style={s.headerSlogan} fixed>Tu aliado electrónico</Text>
        <Text style={s.headerDocTitle} fixed>AUTORIZACIÓN DE PROPUESTA COMERCIAL</Text>
        <Text style={s.headerDate} fixed>Fecha: {date}</Text>
    </>
);

/** Footer fijo — se repite en cada página */
const PageFooter = ({ dateTime }) => (
    <>
        <View style={s.footerBar} fixed />
        <View style={s.footerStripe} fixed />
        <Text style={s.footerLeft1} fixed>TAE-COL SAS  |  Tu aliado electrónico</Text>
        <Text style={s.footerLeft2} fixed>Generado: {dateTime}</Text>
        <Text
            style={s.footerPage}
            render={({ pageNumber }) => `Página ${pageNumber}`}
            fixed
        />
    </>
);

const formatPrice = (val) => {
    if (!val) return '';
    return '$' + Number(val).toLocaleString('es-CO');   // → $3.526.000
};

export const TaePdfDocumento = ({ data, logoBase64 }) => {
    const now = new Date();
    const date = now.toLocaleDateString('es-CO');
    const datetime = now.toLocaleString('es-CO');

    const fmt = (n) => n != null ? Number(n).toLocaleString('es-CO') : '';

    return (
        <Document>

            {/* ════════════════════════════ PÁGINA 1 ════════════════════════════ */}
            <Page size="LETTER" style={s.page}>
                <PageHeader logoBase64={logoBase64} date={date} />
                <PageFooter dateTime={datetime} />

                {/* 1. Información del cliente */}
                <SectionHeader title="1. INFORMACIÓN DEL CLIENTE" />
                <View style={s.spacer6} />

                {/* Fila 1: Tipo ID + Número ID */}
                <View style={s.fieldRow}>
                    <Text style={[s.fieldLabel, { width: '22%' }]}>Tipo de Identificación:</Text>
                    <Text style={[s.fieldValue, { width: '28%', marginRight: 8 }]}>{data?.typeIdentification}</Text>
                    <Text style={[s.fieldLabel, { width: '24%' }]}>Número de Identificación:</Text>
                    <Text style={[s.fieldValue, { flex: 1 }]}>{data?.identificacion}</Text>
                </View>

                {/* Fila 2: Nombre / Razón Social */}
                <FieldRow
                    label="Nombre Completo / Razón Social:"
                    value={data?.nombreCliente}
                    labelWidth="30%"
                />

                {/* Fila 3: Correo + Teléfono */}
                <View style={s.fieldRow}>
                    <Text style={[s.fieldLabel, { width: '18%' }]}>Correo Electrónico:</Text>
                    <Text style={[s.fieldValue, { flex: 1 }]}>
                        {data?.email}
                    </Text>
                    <Text style={[s.fieldLabel, { width: '18%' }]}>Teléfono / Celular:</Text>
                    <Text style={[s.fieldValue, { flex: 1 }]}>{data?.telefono}</Text>
                </View>

                <View style={s.spacer10} />

                {/* 2. Plan actual y análisis de consumo */}
                <SectionHeader title="2. PLAN ACTUAL Y ANÁLISIS DE CONSUMO" />
                <View style={s.spacer6} />

                {/* Fila: Plan actual + Folios del plan */}
                <View style={s.fieldRow}>
                    <Text style={[s.fieldLabel, { width: '25%' }]}>Plan Contratado Actualmente:</Text>
                    <Text style={[s.fieldValue, { flex: 1 }]}>
                        {data?.planContratado}
                    </Text>
                    <Text style={[s.fieldLabel, { width: '25%' }]}>Cantidad de Folios del Plan:</Text>
                    <Text style={[s.fieldValue, { flex: 1 }]}>
                        {fmt(data?.foliosTotalPlan)}
                    </Text>
                </View>

                {/* Fila: Consumo + Período */}
                <View style={s.fieldRow}>
                    <Text style={[s.fieldLabel, { width: '25%' }]}>Consumo Registrado (Folios):</Text>
                    <Text style={[s.fieldValue, { flex: 1 }]}>
                        {fmt(data?.facturasEmitidas)}
                    </Text>
                    <Text style={[s.fieldLabel, { width: '25%' }]}>Período de Análisis:</Text>
                    <Text style={[s.fieldValue, { flex: 1 }]}>{formatDateddMMMyyyy(data?.fechaIni)} / {formatDateddMMMyyyy(data?.fechaFin)}</Text>
                </View>

                {/* Caja de pronóstico */}
                <View style={s.forecastBox}>
                    <Text style={s.forecastText}>
                        De acuerdo con el consumo registrado de{' '}
                        <Text style={s.forecastBold}>
                            {fmt(data?.foliosTotalPlan)} folios
                        </Text>
                        {' '}en el período analizado, el sistema{' '}
                        <Text style={s.forecastBold}>pronostica y sugiere</Text>
                        {' '}el siguiente plan como la opción más adecuada para sus necesidades:
                    </Text>
                </View>

                {/* Fila: Plan sugerido + Folios + Precio */}
                <View style={s.fieldRow}>
                    <Text style={[s.fieldLabel, { width: '16%' }]}>Plan Sugerido:</Text>
                    <Text style={[s.fieldValue, { width: '24%', marginRight: 8 }]}>
                        {data?.nombrePlan}
                    </Text>
                    <Text style={[s.fieldLabel, { width: '22%' }]}>Folios del Plan Sugerido:</Text>
                    <Text style={[s.fieldValue, { width: '16%', marginRight: 8 }]}>
                        {fmt(data?.foliosPlan)}
                    </Text>
                    <Text style={[s.fieldLabel, { width: '8%' }]}>Precio:</Text>
                    <Text style={[s.fieldValue, { flex: 1 }]}>{formatPrice(data?.valorTotal)}</Text>
                </View>

                {/* Nota sobre la sugerencia de plan */}
                <View style={s.suggestedPlanNote}>
                    <Text style={s.suggestedPlanNoteText}>
                        La sugerencia de plan se basa en el consumo del año culminado y estará sujeta al consumo real de folios del año en curso y nueva renovación.
                    </Text>
                </View>

                <View style={s.spacer10} />

                {/* 3. Tabla de planes */}
                <SectionHeader title="3. PLANES TAE-COL SAS 2026" />
                <View style={s.spacer6} />

                {/* Cabecera */}
                <View style={s.tableHeader}>
                    <Text style={[s.thCell, s.col25]}>PLAN</Text>
                    <Text style={[s.thCell, s.col40]}>CANTIDAD DE FOLIOS</Text>
                    <Text style={[s.thCell, s.col35, { textAlign: 'right' }]}>PRECIO (COP)</Text>
                </View>

                {/* Filas */}
                {PLANS.map((row, i) => (
                    <View
                        key={row.plan}
                        style={[s.tableRow, { backgroundColor: i % 2 === 0 ? C.white : C.altRow }]}
                    >
                        <Text style={[s.tdCell, s.col25]}>{row.plan}</Text>
                        <Text style={[s.tdCell, s.col40]}>{row.folios}</Text>
                        <Text style={[s.tdRight, s.col35]}>{row.price}</Text>
                    </View>
                ))}

            </Page>

            {/* ════════════════════════════ PÁGINA 2 ════════════════════════════ */}
            <Page size="LETTER" style={s.page}>
                <PageHeader logoBase64={logoBase64} date={date} />
                <PageFooter dateTime={datetime} />

                {/* 4. Términos y condiciones */}
                <SectionHeader title="4. TÉRMINOS Y CONDICIONES" />
                <View style={s.spacer6} />

                {TERMS.map((term, i) => (
                    <View key={i} style={s.termRow}>
                        <Text style={s.termNum}>{i + 1}.</Text>
                        <Text style={s.termText}>{term}</Text>
                    </View>
                ))}

                <View style={s.spacer10} />

                {/* 5. Autorización y firma */}
                <SectionHeader title="5. AUTORIZACIÓN Y FIRMA" />
                <View style={s.spacer6} />

                <Text style={[s.termText, { marginBottom: 0 }]}>
                    Yo, el/la suscrito(a), en mi calidad de cliente y/o representante autorizado(a),{' '}
                    <Text style={s.forecastBold}>
                        manifiesto que he leído, entendido y acepto
                    </Text>
                    {' '}la presente Propuesta Comercial de TAE-COL SAS, incluyendo el plan
                    seleccionado, los precios indicados y los términos y condiciones expuestos
                    en este documento.
                </Text>

                <View style={s.spacer18} />

                {/* Bloque de firmas — dos columnas */}
                <View style={s.sigBlock}>
                    {[
                        { label: 'Firma del Cliente / Representante' },
                        { label: 'Firma Asesor TAE-COL SAS' },
                    ].map(({ label }) => (
                        <View key={label} style={s.sigCol}>
                            <View style={s.sigLine} />
                            <Text style={s.sigSubLabel}>{label}</Text>
                            {['Nombre:', 'Identificación:'].map((f) => (
                                <View key={f} style={[s.fieldRow, { marginBottom: 10 }]}>
                                    <Text style={[s.fieldLabel, { width: '40%' }]}>{f}</Text>
                                    <Text style={s.fieldValue} />
                                </View>
                            ))}
                        </View>
                    ))}
                </View>

                {/* Aviso de confidencialidad */}
                <View style={s.noticeBox}>
                    <Text style={s.noticeText}>
                        Este documento es de carácter CONFIDENCIAL y de uso exclusivo entre
                        TAE-COL SAS y el cliente indicado. Queda prohibida su reproducción o
                        divulgación sin autorización expresa.
                    </Text>
                </View>

            </Page>

        </Document>
    )
}

export default TaePdfDocumento;